/**
 * Converts RGBA color stored as integer to hexadecimal web-compatible format
 * @param {number} rgbaInt - RGBA color as integer (32-bit)
 * @param {boolean} includeAlpha - Whether to include alpha channel in output (default: false)
 * @returns {string} Hexadecimal color string (#RRGGBB or #RRGGBBAA)
 */
export function rgbaIntToHex(rgbaInt, includeAlpha = false) {
  // Extract RGBA components from 32-bit integer
  // Assuming format: RRGGBBAA (red in bits 31-24, green in 23-16, blue in 15-8, alpha in 7-0)
  const red = (rgbaInt >>> 24) & 0xFF;
  const green = (rgbaInt >>> 16) & 0xFF;
  const blue = (rgbaInt >>> 8) & 0xFF;
  const alpha = rgbaInt & 0xFF;

  // Convert to hex with padding
  const redHex = red.toString(16).padStart(2, '0');
  const greenHex = green.toString(16).padStart(2, '0');
  const blueHex = blue.toString(16).padStart(2, '0');
  const alphaHex = alpha.toString(16).padStart(2, '0');

  if (includeAlpha) {
    return `#${redHex}${greenHex}${blueHex}${alphaHex}`;
  }
  
  return `#${redHex}${greenHex}${blueHex}`;
}

/**
 * Alternative function for ARGB format (alpha in most significant bits)
 * @param {number} argbInt - ARGB color as integer (32-bit)
 * @param {boolean} includeAlpha - Whether to include alpha channel in output (default: false)
 * @returns {string} Hexadecimal color string (#RRGGBB or #AARRGGBB)
 */
export function argbIntToHex(argbInt, includeAlpha = false) {
  // Extract ARGB components from 32-bit integer
  // Format: AARRGGBB (alpha in bits 31-24, red in 23-16, green in 15-8, blue in 7-0)
  const alpha = (argbInt >>> 24) & 0xFF;
  const red = (argbInt >>> 16) & 0xFF;
  const green = (argbInt >>> 8) & 0xFF;
  const blue = argbInt & 0xFF;

  // Convert to hex with padding
  const redHex = red.toString(16).padStart(2, '0');
  const greenHex = green.toString(16).padStart(2, '0');
  const blueHex = blue.toString(16).padStart(2, '0');
  const alphaHex = alpha.toString(16).padStart(2, '0');

  if (includeAlpha) {
    return `#${alphaHex}${redHex}${greenHex}${blueHex}`;
  }
  
  return `#${redHex}${greenHex}${blueHex}`;
}

/**
 * Converts hexadecimal color to RGBA integer
 * @param {string} hexColor - Hexadecimal color string (#RRGGBB or #RRGGBBAA)
 * @returns {number} RGBA color as integer
 */
export function hexToRgbaInt(hexColor) {
  // Remove # if present
  const hex = hexColor.replace('#', '');
  
  let red, green, blue, alpha = 255;
  
  if (hex.length === 6) {
    // #RRGGBB format
    red = parseInt(hex.substring(0, 2), 16);
    green = parseInt(hex.substring(2, 4), 16);
    blue = parseInt(hex.substring(4, 6), 16);
  } else if (hex.length === 8) {
    // #RRGGBBAA format
    red = parseInt(hex.substring(0, 2), 16);
    green = parseInt(hex.substring(2, 4), 16);
    blue = parseInt(hex.substring(4, 6), 16);
    alpha = parseInt(hex.substring(6, 8), 16);
  } else {
    throw new Error('Invalid hex color format. Expected #RRGGBB or #RRGGBBAA');
  }
  
  // Combine into 32-bit integer (RRGGBBAA format)
  return (red << 24) | (green << 16) | (blue << 8) | alpha;
}

/**
 * Example usage and test function
 */
export function testColorConversions() {
  console.log('Color Conversion Tests:');
  
  // Test RGBA to Hex
  const rgbaColor = 0xFF6B3AFF; // Red=255, Green=107, Blue=58, Alpha=255
  console.log(`RGBA Int: ${rgbaColor} -> Hex: ${rgbaIntToHex(rgbaColor)}`);
  console.log(`RGBA Int: ${rgbaColor} -> Hex with Alpha: ${rgbaIntToHex(rgbaColor, true)}`);
  
  // Test ARGB to Hex
  const argbColor = 0xFF6B3AFF; // Alpha=255, Red=107, Green=58, Blue=255
  console.log(`ARGB Int: ${argbColor} -> Hex: ${argbIntToHex(argbColor)}`);
  console.log(`ARGB Int: ${argbColor} -> Hex with Alpha: ${argbIntToHex(argbColor, true)}`);
  
  // Test Hex to RGBA Int
  const hexColor = '#FF6B3A';
  console.log(`Hex: ${hexColor} -> RGBA Int: ${hexToRgbaInt(hexColor)}`);
}